import styled from "styled-components";
import { DateTime } from "luxon";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { Button } from "../../components/Button";
import { Image } from "../../components/Image";
import { checksStatusName } from "../IndividualView/helper";
import { isUnsignedActivity } from "../../utils/monitoringScreening";
import { ALL_ACTIVITY, TimelineSelection } from "./useMonitoringScreeningModel";
import success from "../../assets/card-success-icon.svg";
import inReview from "../../assets/card-in-review-icon.svg";
import pending from "../../assets/pending-icon.svg";
import manuallyAccepted from "../../assets/manually_accepted.svg";
import manuallyRejected from "../../assets/manually_rejected.svg";

/**
 * The timeline of monitoring alerts, and the page's navigation.
 *
 * Vertical and newest at the top, deliberately not left-to-right chronology:
 * the work is always at the newest end, and this is a control before it is a
 * history. "All activity" is the head of the list rather than a separate mode,
 * so the accumulated view is a wider scope of the same screen.
 *
 * Drawn as a conventional timeline, a continuous spine with a node per entry,
 * rather than a stack of chips. The status icons are the ones the activity table
 * already uses, so an alert reads the same wherever it appears.
 */

// same icon set as the monitoring activity table, so an alert reads the same
// wherever it is shown
const STATUS_ICONS: Record<string, string> = {
  accepted: success,
  review: inReview,
  pending,
  manually_accepted: manuallyAccepted,
  manually_rejected: manuallyRejected,
};

const SPINE_LEFT = "15px";

const Entry = styled.button<{
  isSelected: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}>`
  ${({
    theme: { colors, fontFamily, radii, space },
    isSelected,
    isFirst,
    isLast,
  }) => `
    position: relative;
    display: block;
    width: 100%;
    text-align: left;
    font-family: ${fontFamily};
    cursor: pointer;
    padding: ${space[1]} ${space[1]} ${space[1]} ${space[4]};
    border-radius: ${radii[0]};
    border: 1px solid ${isSelected ? colors.blue[10] : "transparent"};
    background: ${isSelected ? colors.white : "transparent"};

    /* the spine, stopping at the node on the first and last entries so the
       line reads as bounded rather than running off the ends */
    &::before {
      content: "";
      position: absolute;
      left: ${SPINE_LEFT};
      top: ${isFirst ? "18px" : "0"};
      bottom: ${isLast ? "calc(100% - 18px)" : "0"};
      width: 2px;
      background: ${colors.gray[20]};
    }

    &:hover {
      background: ${isSelected ? colors.white : colors.gray[10]};
    }

    &:focus-visible {
      outline: 2px solid ${colors.blue.primary};
      outline-offset: 2px;
    }
  `}
`;

const Node = styled.span<{ isSelected: boolean; needsAttention: boolean }>`
  ${({ theme: { colors }, isSelected, needsAttention }) => {
    const fill = needsAttention
      ? colors.orange[50]
      : isSelected
      ? colors.blue.primary
      : colors.white;
    return `
      position: absolute;
      left: 10px;
      top: 12px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      box-sizing: border-box;
      background: ${fill};
      border: 2px solid ${
        needsAttention
          ? colors.orange[50]
          : isSelected
          ? colors.blue.primary
          : colors.gray[20]
      };
      /* lifts the node clear of the spine running behind it */
      box-shadow: 0 0 0 3px ${colors.gray[5]};
    `;
  }}
`;

const formatEntryDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat("dd MMM yyyy") : "-";
};

interface Props {
  activities: any[];
  selection: TimelineSelection;
  onSelect: (selection: TimelineSelection) => void;
  /** Matches on the whole record, shown against All activity. */
  matchCount: number;
  /** How many results each activity contributed, keyed on activity id. */
  resultCountByActivityId: Record<number, number>;
  notLoadedCount: number;
  onLoadMore: () => void;
  /** Legacy activities open their own sliders instead of scoping the page. */
  legacyActivities: any[];
  onSelectLegacy: (activity: any) => void;
}

export const MonitoringTimelineRail = ({
  activities,
  selection,
  onSelect,
  matchCount,
  resultCountByActivityId,
  notLoadedCount,
  onLoadMore,
  legacyActivities,
  onSelectLegacy,
}: Props) => (
  // a record with many alerts must stay reachable once the rail is stuck, so the
  // list scrolls inside itself rather than running past the bottom of the screen
  <Box
    bg="gray.5"
    borderRadius="12px"
    p={1}
    maxHeight="calc(100vh - 280px)"
    overflowY="auto"
  >
    <Entry
      type="button"
      isFirst
      isSelected={selection === ALL_ACTIVITY}
      onClick={() => onSelect(ALL_ACTIVITY)}
    >
      <Node isSelected={selection === ALL_ACTIVITY} needsAttention={false} />
      <Paragraph mt={0} mb={0} fontWeight="600" color="gray.80">
        All activity
      </Paragraph>
      <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
        {matchCount} match{matchCount === 1 ? "" : "es"}
      </Paragraph>
    </Entry>

    {activities.map((activity, index) => {
      const resultCount = resultCountByActivityId[activity.id];
      const needsAttention = isUnsignedActivity(activity);
      const statusKey = activity?.status?.status;
      const isLast =
        index === activities.length - 1 &&
        notLoadedCount === 0 &&
        legacyActivities.length === 0;

      return (
        <Entry
          key={activity.id}
          type="button"
          isSelected={selection === activity.id}
          isLast={isLast}
          onClick={() => onSelect(activity.id)}
        >
          <Node
            isSelected={selection === activity.id}
            needsAttention={needsAttention}
          />
          <Flex justifyContent="space-between" alignItems="center" gap="8">
            <Paragraph mt={0} mb={0} fontWeight="600" color="gray.80">
              {formatEntryDate(activity.created_on)}
            </Paragraph>
            {/* the eye goes to the alerts still waiting on someone */}
            {STATUS_ICONS[statusKey] && (
              <Image
                src={STATUS_ICONS[statusKey]}
                alt={checksStatusName[statusKey] ?? statusKey}
                height="auto"
                width="18px"
              />
            )}
          </Flex>
          <Paragraph
            mt={0}
            mb={0}
            fontSize={0}
            color={needsAttention ? "orange.50" : "gray.50"}
            fontWeight={needsAttention ? "600" : "400"}
          >
            {checksStatusName[statusKey] ?? statusKey ?? "-"}
          </Paragraph>
          {resultCount != null && (
            <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
              {resultCount} result{resultCount === 1 ? "" : "s"}
            </Paragraph>
          )}
        </Entry>
      );
    })}

    {/* stated, never truncated quietly: a page that covers part of the record
        while presenting itself as the whole record is worse than one that
        covers less and says so */}
    {notLoadedCount > 0 && (
      <Box pl={4} py={1}>
        <Paragraph mt={0} mb={1} fontSize={0} color="gray.50">
          {notLoadedCount} older alert{notLoadedCount === 1 ? "" : "s"} not
          loaded.
        </Paragraph>
        <Button variant="text" size="small" px={0} onClick={onLoadMore}>
          Load them
        </Button>
      </Box>
    )}

    {legacyActivities.length > 0 && (
      <Box mt={2} pt={2} borderTop="1px solid" borderTopColor="gray.20">
        <Paragraph mt={0} mb={1} pl={4} fontSize={0} color="gray.50">
          Earlier monitoring
        </Paragraph>
        {legacyActivities.map((activity, index) => (
          <Entry
            key={activity.id}
            type="button"
            isSelected={false}
            isFirst={index === 0}
            isLast={index === legacyActivities.length - 1}
            onClick={() => onSelectLegacy(activity)}
          >
            <Node isSelected={false} needsAttention={false} />
            <Paragraph mt={0} mb={0} fontWeight="500" color="gray.80">
              {activity.event}
            </Paragraph>
            <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
              {formatEntryDate(activity.created_on)}
            </Paragraph>
          </Entry>
        ))}
      </Box>
    )}
  </Box>
);
